import React, { useState, useEffect } from 'react';
import { useLMS } from '../../context/LMSContext';
import { supabase, db } from '../../lib/supabase';
import { itemTypeIcons, ZOOM_LOGO_URL, DEFAULT_ZOOM_URL } from '../../data/lmsData';
import RichTextEditor from '../shared/RichTextEditor';
import EligibilityToggle from '../shared/EligibilityToggle';

const MasterModules = () => {
  const {
    currentUser,
    modules,
    addModule,
    updateModule,
    deleteModule,
    addModuleItem,
    updateModuleItem,
    deleteModuleItem,
    setCurrentView
  } = useLMS();

  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [showAddModuleModal, setShowAddModuleModal] = useState(false);
  const [showEditModuleModal, setShowEditModuleModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [editingModule, setEditingModule] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [schedulingModule, setSchedulingModule] = useState(null);
  const [scheduleWeek, setScheduleWeek] = useState('');
  const [scheduleDay, setScheduleDay] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // =========================================================================
  // DEFAULT ZOOM URL SETTINGS
  // =========================================================================
  const [defaultZoomUrl, setDefaultZoomUrl] = useState(DEFAULT_ZOOM_URL);
  const [showZoomSettingsModal, setShowZoomSettingsModal] = useState(false);
  const [tempZoomUrl, setTempZoomUrl] = useState('');
  const [isSavingZoomUrl, setIsSavingZoomUrl] = useState(false);

  // Load default zoom URL from settings
  useEffect(() => {
    const loadZoomSetting = async () => {
      try {
        const { data } = await db.getSetting('default_zoom_url');
        if (data?.value) {
          setDefaultZoomUrl(data.value);
        }
      } catch (err) {
        console.log('Using fallback zoom URL');
      }
    };
    loadZoomSetting();
  }, []);

  const saveDefaultZoomUrl = async () => {
    setIsSavingZoomUrl(true);
    try {
      await db.upsertSetting('default_zoom_url', tempZoomUrl, 'Default Zoom meeting URL for all live lectures', currentUser?.id);
      setDefaultZoomUrl(tempZoomUrl);
      setShowZoomSettingsModal(false);
    } catch (err) {
      console.error('Error saving zoom URL:', err);
      alert('Failed to save Zoom URL');
    }
    setIsSavingZoomUrl(false);
  };

  // =========================================================================
  // MASTER SYLLABUS STATE
  // =========================================================================
  const [masterPdfs, setMasterPdfs] = useState([]);
  const [isLoadingPdfs, setIsLoadingPdfs] = useState(true);
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);
  const [showAddPdfModal, setShowAddPdfModal] = useState(false);
  const [newPdfTitle, setNewPdfTitle] = useState('');
  const [selectedPdfFile, setSelectedPdfFile] = useState(null);
  const [syllabusExpanded, setSyllabusExpanded] = useState(true);

  // Drag and drop state
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const [newModule, setNewModule] = useState({
    weekNumber: '',
    title: '',
    isVisible: false
  });

  const [newItem, setNewItem] = useState({
    type: 'video',
    title: '',
    content: '',
    description: '',
    videoUrl: '',
    videoType: 'youtube',
    dueDay: '',
    points: 0,
    questionCount: 0,
    timeLimit: 0,
    startTime: '',
    endTime: '',
    pdfUrl: '',
    pdfName: '',
    countsForEligibility: false
  });

  // PDF upload state - supports multiple files (up to 10)
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [selectedFilesForItem, setSelectedFilesForItem] = useState([]);
  const [selectedFilesForEdit, setSelectedFilesForEdit] = useState([]);
  const [removePdfOnEdit, setRemovePdfOnEdit] = useState(false);

  // Reflection questions state (for add/edit item when type='reflection')
  const [reflectionQuestions, setReflectionQuestions] = useState(['']);

  // Sort modules by week number
  const sortedModules = [...(modules || [])].sort((a, b) => 
    (a.weekNumber || a.week_number) - (b.weekNumber || b.week_number)
  );

  const isAdmin = currentUser?.role === 'admin';
  const isProfessor = currentUser?.role === 'professor';
  const isStaff = isAdmin || isProfessor;

  // =========================================================================
  // LOAD MASTER PDFS (cohort_id IS NULL)
  // =========================================================================
  useEffect(() => {
    const loadMasterPdfs = async () => {
      setIsLoadingPdfs(true);
      try {
        const { data, error } = await supabase
          .from('syllabus_pdfs')
          .select('*')
          .is('cohort_id', null)
          .order('uploaded_at', { ascending: false });

        if (error) throw error;
        setMasterPdfs(data || []);
      } catch (err) {
        console.error('Error loading master PDFs:', err);
      } finally {
        setIsLoadingPdfs(false);
      }
    };

    if (isStaff) {
      loadMasterPdfs();
    }
  }, [isStaff]);

  // =========================================================================
  // UPLOAD MASTER PDF
  // =========================================================================
  const handleUploadMasterPdf = async (e) => {
    e.preventDefault();
    if (!selectedPdfFile || !newPdfTitle.trim()) return;

    setIsUploadingPdf(true);
    try {
      // Generate unique filename
      const fileExt = selectedPdfFile.name.split('.').pop();
      const timestamp = Date.now();
      const sanitizedTitle = newPdfTitle.trim().replace(/[^a-zA-Z0-9]/g, '_');
      const filePath = `master/${timestamp}_${sanitizedTitle}.${fileExt}`;

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('syllabus-pdfs')
        .upload(filePath, selectedPdfFile);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('syllabus-pdfs')
        .getPublicUrl(filePath);

      const fileUrl = urlData.publicUrl;

      // Insert record into syllabus_pdfs with NULL cohort_id
      const { data: pdfRecord, error: dbError } = await supabase
        .from('syllabus_pdfs')
        .insert({
          cohort_id: null, // NULL indicates master/global PDF
          title: newPdfTitle.trim(),
          filename: selectedPdfFile.name,
          file_url: fileUrl,
          file_size: formatFileSize(selectedPdfFile.size)
        })
        .select()
        .single();

      if (dbError) throw dbError;

      // Update local state
      setMasterPdfs(prev => [pdfRecord, ...prev]);
      
      // Reset form
      setShowAddPdfModal(false);
      setNewPdfTitle('');
      setSelectedPdfFile(null);

    } catch (err) {
      console.error('Error uploading master PDF:', err);
      alert(`Failed to upload PDF: ${err.message}`);
    } finally {
      setIsUploadingPdf(false);
    }
  };

  // =========================================================================
  // DELETE MASTER PDF
  // =========================================================================
  const handleDeleteMasterPdf = async (pdf) => {
    if (!window.confirm(`Delete "${pdf.title}"? This will remove it from ALL cohorts.`)) {
      return;
    }

    try {
      // Extract file path from URL for storage deletion
      const urlParts = pdf.file_url.split('/syllabus-pdfs/');
      const filePath = urlParts[1] ? decodeURIComponent(urlParts[1]) : null;

      // Delete from storage (if we can determine the path)
      if (filePath) {
        await supabase.storage
          .from('syllabus-pdfs')
          .remove([filePath]);
      }

      // Delete from database
      const { error } = await supabase
        .from('syllabus_pdfs')
        .delete()
        .eq('id', pdf.id);

      if (error) throw error;

      // Update local state
      setMasterPdfs(prev => prev.filter(p => p.id !== pdf.id));

    } catch (err) {
      console.error('Error deleting master PDF:', err);
      alert(`Failed to delete PDF: ${err.message}`);
    }
  };

  // =========================================================================
  // HELPER: Format file size
  // =========================================================================
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) return `${mb.toFixed(1)} MB`;
    const kb = bytes / 1024;
    return `${kb.toFixed(0)} KB`;
  };

  if (!isStaff) {
    return (
      <div className="master-modules">
        <div className="empty-state">
          <h2>Access Denied</h2>
          <p>Only administrators and professors can access this section.</p>
        </div>
      </div>
    );
  }

  const toggleWeekExpanded = (weekNumber) => {
    setExpandedWeeks(prev => ({
      ...prev,
      [weekNumber]: !prev[weekNumber]
    }));
  };

  const handleToggleVisibility = async (module) => {
    const newVisibility = !module.isVisible;
    const result = await updateModule(module.id, {
      title: module.title,
      isVisible: newVisibility,
      visibleDate: null // Clear scheduled date when manually toggling
    });
    
    if (result.error) {
      alert(`Failed to update visibility: ${result.error}`);
    }
  };

  const openScheduleModal = (module) => {
    const weekNum = module.weekNumber || module.week_number;
    setSchedulingModule(module);
    // Parse existing schedule if present (stored as "week:day" e.g. "3:Thursday")
    if (module.visibleAfter) {
      const [week, day] = module.visibleAfter.split(':');
      setScheduleWeek(week || '');
      setScheduleDay(day || '');
    } else {
      // Default to previous week, Monday (so Week 4 defaults to "Week 3, Monday")
      setScheduleWeek(String(Math.max(1, weekNum - 1)));
      setScheduleDay('Monday');
    }
    setShowScheduleModal(true);
  };

  const handleScheduleVisibility = async (e) => {
    e.preventDefault();
    if (!schedulingModule) return;
    
    setIsSubmitting(true);
    
    // Store as "week:day" format (e.g., "3:Thursday")
    const visibleAfter = scheduleWeek && scheduleDay ? `${scheduleWeek}:${scheduleDay}` : null;
    
    const result = await updateModule(schedulingModule.id, {
      title: schedulingModule.title,
      isVisible: false, // Keep hidden until scheduled time
      visibleAfter: visibleAfter
    });
    
    setIsSubmitting(false);
    
    if (result.error) {
      alert(`Failed to schedule visibility: ${result.error}`);
      return;
    }
    
    setShowScheduleModal(false);
    setSchedulingModule(null);
    setScheduleWeek('');
    setScheduleDay('');
  };

  const handleClearSchedule = async () => {
    if (!schedulingModule) return;
    
    setIsSubmitting(true);
    
    const result = await updateModule(schedulingModule.id, {
      title: schedulingModule.title,
      isVisible: false,
      visibleAfter: null
    });
    
    setIsSubmitting(false);
    
    if (result.error) {
      alert(`Failed to clear schedule: ${result.error}`);
      return;
    }
    
    setShowScheduleModal(false);
    setSchedulingModule(null);
    setScheduleWeek('');
    setScheduleDay('');
  };

  const openAddItemModal = (weekNumber) => {
    setSelectedWeek(weekNumber);
    // Preserve dueDay if it was pre-set (e.g., from clicking + on a day)
    setNewItem(prev => ({
      type: 'video',
      title: '',
      content: '',
      description: '',
      videoUrl: '',
      videoType: 'youtube',
      dueDay: prev.dueDay || '', // Keep pre-set day
      points: 0,
      questionCount: 0,
      timeLimit: 0,
      startTime: '',
      endTime: '',
      countsForEligibility: false
    }));
    setReflectionQuestions(['']);
    setShowAddItemModal(true);
  };

  const openEditModuleModal = (module) => {
    setEditingModule({
      id: module.id,
      weekNumber: module.weekNumber || module.week_number,
      title: module.title?.replace(`Week ${module.weekNumber || module.week_number}: `, '') || '',
      isVisible: module.isVisible ?? module.is_visible ?? false
    });
    setShowEditModuleModal(true);
  };

  const openEditItemModal = (weekNumber, item) => {
    setSelectedWeek(weekNumber);
    setEditingItem({
      ...item,
      videoUrl: item.videoUrl || item.video_url || '',
      videoType: item.videoType || item.video_type || 'youtube',
      dueDay: item.dueDay || item.due_day || '',
      description: item.description || '',
      content: item.content || '',
      startTime: item.startTime || item.start_time || '',
      endTime: item.endTime || item.end_time || '',
      points: item.points || 0,
      questionCount: item.questionCount || item.question_count || 0,
      timeLimit: item.timeLimit || item.time_limit || 0,
      pdfUrl: item.pdfUrl || item.pdf_url || '',
      pdfName: item.pdfName || item.pdf_name || '',
      attachments: item.attachments || null,
      _originalAttachments: item.attachments || null,
      countsForEligibility: item.countsForEligibility || item.counts_for_eligibility || false
    });
    setSelectedFilesForEdit([]);
    setRemovePdfOnEdit(false);
    setShowEditItemModal(true);
  };

  const handleAddModule = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const result = await addModule({
      weekNumber: parseInt(newModule.weekNumber),
      title: newModule.title || `Week ${newModule.weekNumber}`,
      isVisible: newModule.isVisible
    });

    setIsSubmitting(false);

    if (result.error) {
      alert(`Failed to create module: ${result.error}`);
      return;
    }

    setShowAddModuleModal(false);
    setNewModule({ weekNumber: '', title: '', isVisible: false });
  };

  const handleEditModule = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const weekNum = editingModule.weekNumber;
    const fullTitle = `Week ${weekNum}: ${editingModule.title}`;

    const result = await updateModule(editingModule.id, {
      title: fullTitle,
      isVisible: editingModule.isVisible
    });

    setIsSubmitting(false);

    if (result.error) {
      alert(`Failed to update module: ${result.error}`);
      return;
    }

    setShowEditModuleModal(false);
    setEditingModule(null);
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Find the module by week number to get its ID
    const module = modules.find(m => (m.weekNumber || m.week_number) === selectedWeek);
    if (!module) {
      alert('Module not found');
      setIsSubmitting(false);
      return;
    }

    // Upload files if selected (supports multiple)
    let pdfUrl = newItem.pdfUrl;
    let pdfName = newItem.pdfName;
    let attachments = null;
    
    if (selectedFilesForItem.length > 0) {
      setUploadingPdf(true);
      try {
        const uploaded = [];
        for (const file of selectedFilesForItem) {
          const fileExt = file.name.split('.').pop();
          const fileName = `workbooks/${module.id}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${fileExt}`;
          
          const { data, error } = await supabase.storage
            .from('syllabus-pdfs')
            .upload(fileName, file, {
              contentType: file.type || 'application/pdf',
              cacheControl: '3600',
              upsert: false
            });
          
          if (error) {
            alert(`Failed to upload ${file.name}: ${error.message}`);
            setIsSubmitting(false);
            setUploadingPdf(false);
            return;
          }
          
          const { data: urlData } = supabase.storage
            .from('syllabus-pdfs')
            .getPublicUrl(fileName);
          
          uploaded.push({
            url: urlData.publicUrl,
            name: file.name,
            size: file.size,
            type: file.type || 'application/pdf'
          });
        }
        
        // First file also populates legacy pdf_url/pdf_name for backwards compatibility
        if (uploaded.length > 0) {
          pdfUrl = uploaded[0].url;
          pdfName = uploaded[0].name;
        }
        // Store all files in attachments JSON
        if (uploaded.length > 0) {
          attachments = uploaded;
        }
      } catch (err) {
        alert(`Failed to upload files: ${err.message}`);
        setIsSubmitting(false);
        setUploadingPdf(false);
        return;
      }
      setUploadingPdf(false);
    }

    // Determine which days to create items for
    let daysToCreate = [newItem.dueDay || ''];

    if (newItem.type === 'live-lecture' && newItem.isRecurring && newItem.recurringDays) {
      const recurringMap = {
        'monday': ['Monday'],
        'tuesday': ['Tuesday'],
        'wednesday': ['Wednesday'],
        'thursday': ['Thursday'],
        'friday': ['Friday'],
        'mwf': ['Monday', 'Wednesday', 'Friday'],
        'tth': ['Tuesday', 'Thursday'],
        'daily': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      };
      daysToCreate = recurringMap[newItem.recurringDays] || [newItem.dueDay || ''];
    }

    let lastError = null;
    for (const day of daysToCreate) {
      const result = await addModuleItem(module.id, {
        type: newItem.type,
        title: newItem.title,
        content: newItem.content,
        description: newItem.description,
        videoUrl: newItem.videoUrl,
        videoType: newItem.videoType,
        dueDay: day,
        points: parseInt(newItem.points) || 0,
        questionCount: parseInt(newItem.questionCount) || 0,
        timeLimit: parseInt(newItem.timeLimit) || 0,
        startTime: newItem.startTime || null,
        endTime: newItem.endTime || null,
        pdfUrl: pdfUrl || null,
        pdfName: pdfName || null,
        attachments: attachments || null,
        countsForEligibility: newItem.countsForEligibility || false
      });

      if (result.error) {
        lastError = result.error;
      }
    }

    setIsSubmitting(false);
    setSelectedFilesForItem([]);

    if (lastError) {
      alert(`Failed to add item: ${lastError}`);
      return;
    }

    // If this is a reflection item, create the associated reflection questions
    if (newItem.type === 'reflection') {
      const validQuestions = reflectionQuestions.filter(q => q.trim());
      if (validQuestions.length > 0) {
        try {
          for (let i = 0; i < validQuestions.length; i++) {
            // Supabase returns errors rather than throwing, so check each one -
            // otherwise a rejected insert looks like a successful save.
            const { error } = await db.createReflectionQuestion({
              moduleId: module.id,
              questionText: validQuestions[i].trim(),
              questionOrder: i + 1,
              createdBy: currentUser.id
            });
            if (error) throw error;
          }
        } catch (err) {
          console.error('Error creating reflection questions:', err);
          alert(`Reflection item created, but the questions failed to save: ${err.message || err}\n\nYou can add them from the Reflections page in the sidebar.`);
        }
      }
    }

    setShowAddItemModal(false);
    // Auto-expand the week to show the new item
    setExpandedWeeks(prev => ({ ...prev, [selectedWeek]: true }));
  };

  const handleEditItem = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    let pdfUrl = editingItem.pdfUrl;
    let pdfName = editingItem.pdfName;
    // Only send attachments to the database when we have actual file data to save.
    // Using undefined means the payload stripper will exclude it entirely,
    // which is safe even if the column hasn't been created yet.
    let attachments = undefined;

    // Handle PDF/attachments removal
    if (removePdfOnEdit) {
      pdfUrl = null;
      pdfName = null;
      attachments = null; // explicitly clear
    }

    // Detect if user removed individual files via the edit modal UI
    if (!removePdfOnEdit && editingItem._originalAttachments) {
      const origLen = editingItem._originalAttachments.length;
      const currLen = (editingItem.attachments || []).length;
      if (currLen < origLen) {
        // Files were individually removed
        attachments = editingItem.attachments && editingItem.attachments.length > 0 ? editingItem.attachments : null;
        if (attachments && attachments.length > 0) {
          pdfUrl = attachments[0].url;
          pdfName = attachments[0].name;
        } else {
          pdfUrl = null;
          pdfName = null;
        }
      }
    }

    // Handle new file uploads (append to existing attachments)
    if (selectedFilesForEdit.length > 0) {
      try {
        const uploaded = [];
        for (const file of selectedFilesForEdit) {
          const fileExt = file.name.split('.').pop();
          const fileName = `workbooks/edit_${editingItem.id}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${fileExt}`;

          const { error: uploadError } = await supabase.storage
            .from('syllabus-pdfs')
            .upload(fileName, file, {
              contentType: file.type || 'application/pdf',
              upsert: true
            });

          if (uploadError) {
            console.error('File upload error:', uploadError);
            alert(`Failed to upload ${file.name}. Skipping.`);
            continue;
          }
          
          const { data: urlData } = supabase.storage
            .from('syllabus-pdfs')
            .getPublicUrl(fileName);
          
          uploaded.push({
            url: urlData.publicUrl,
            name: file.name,
            size: file.size,
            type: file.type || 'application/pdf'
          });
        }

        if (uploaded.length > 0) {
          // Merge with existing attachments (if not removing all)
          const existing = (!removePdfOnEdit && editingItem.attachments) ? editingItem.attachments : [];
          attachments = [...existing, ...uploaded];
          
          // Keep legacy fields pointing to first attachment
          pdfUrl = attachments[0].url;
          pdfName = attachments[0].name;
        }
      } catch (err) {
        console.error('File upload error:', err);
      }
    }

    const result = await updateModuleItem(editingItem.id, {
      title: editingItem.title,
      content: editingItem.content,
      description: editingItem.description,
      videoUrl: editingItem.videoUrl,
      dueDay: editingItem.dueDay,
      points: parseInt(editingItem.points) || 0,
      questionCount: parseInt(editingItem.questionCount) || 0,
      timeLimit: parseInt(editingItem.timeLimit) || 0,
      startTime: editingItem.startTime || null,
      endTime: editingItem.endTime || null,
      pdfUrl: pdfUrl || null,
      pdfName: pdfName || null,
      attachments: attachments || null,
      countsForEligibility: editingItem.countsForEligibility || false
    });

    setIsSubmitting(false);

    if (result.error) {
      alert(`Failed to update item: ${result.error}`);
      return;
    }

    setShowEditItemModal(false);
    setEditingItem(null);
    setSelectedFilesForEdit([]);
    setRemovePdfOnEdit(false);
  };

  const handleDeleteItem = async (weekNumber, itemId, itemTitle) => {
    if (!window.confirm(`Delete "${itemTitle}"? This will remove it from ALL cohorts.`)) {
      return;
    }

    const result = await deleteModuleItem(itemId);
    if (result.error) {
      alert(`Failed to delete item: ${result.error}`);
    }
  };

  const getTypeIcon = (type) => {
    if (type === 'live-lecture') {
      return (
        <img 
          src={ZOOM_LOGO_URL} 
          alt="Zoom" 
          className="zoom-icon"
          style={{ width: '20px', height: '20px', objectFit: 'contain' }}
        />
      );
    }
    return itemTypeIcons[type] || '📄';
  };

  const getTypeLabel = (type) => {
    const labels = {
      'start-here': 'Start Here',
      'about-course': 'About Course',
      'video': 'Video',
      'live-lecture': 'Live Lecture',
      'assignment': 'Assignment',
      'quiz': 'Quiz',
      'exam': 'Exam',
      'workbook': 'Workbook',
      'resource': 'Resource',
      'reflection': 'Reflection'
    };
    return labels[type] || type;
  };

  // =========================================================================
  // DRAG AND DROP HANDLERS
  // =========================================================================
  const handleDragStart = (e, item, weekNum) => {
    setDraggedItem({ ...item, sourceWeek: weekNum });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', item.id);
    setTimeout(() => {
      e.target.classList.add('dragging');
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.classList.remove('dragging');
    setDraggedItem(null);
    setDragOverDay(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, day, weekNum) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDay(`${weekNum}-${day}`);
  };

  const handleItemDragOver = (e, day, weekNum, index) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDay(`${weekNum}-${day}`);
    
    // Determine if cursor is in top or bottom half of the item
    const rect = e.currentTarget.getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;
    const dropPos = e.clientY < midpoint ? index : index + 1;
    setDragOverIndex(`${weekNum}-${day}-${dropPos}`);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverDay(null);
      setDragOverIndex(null);
    }
  };

  const handleDrop = async (e, targetDay, weekNum) => {
    e.preventDefault();
    const savedDragOverIndex = dragOverIndex;
    setDragOverDay(null);
    setDragOverIndex(null);

    if (!draggedItem) return;

    const currentDay = draggedItem.dueDay || draggedItem.due_day;
    
    // If dropping on the same day and same week, reorder within that day
    if (currentDay === targetDay && draggedItem.sourceWeek === weekNum) {
      const module = sortedModules.find(m => (m.weekNumber || m.week_number) === weekNum);
      if (module) {
        const dayItems = (module.items || [])
          .filter(item => (item.dueDay || item.due_day) === targetDay)
          .sort((a, b) => (a.itemOrder || a.item_order || 0) - (b.itemOrder || b.item_order || 0));
        
        const dragIndex = dayItems.findIndex(item => item.id === draggedItem.id);
        
        // Get drop index from tracked state
        const indexKey = `${weekNum}-${targetDay}-`;
        let dropIndex = -1;
        if (savedDragOverIndex && savedDragOverIndex.startsWith(indexKey)) {
          dropIndex = parseInt(savedDragOverIndex.replace(indexKey, ''));
        }

        // Fallback: use DOM position
        if (dropIndex === -1) {
          const dropTarget = e.target.closest('.item-row');
          if (dropTarget) {
            const allItemRows = Array.from(e.currentTarget.querySelectorAll('.item-row'));
            const targetIdx = allItemRows.indexOf(dropTarget);
            const rect = dropTarget.getBoundingClientRect();
            dropIndex = e.clientY < rect.top + rect.height / 2 ? targetIdx : targetIdx + 1;
          }
        }

        if (dropIndex !== -1 && dragIndex !== -1) {
          const adjustedDrop = dropIndex > dragIndex ? dropIndex - 1 : dropIndex;
          
          if (adjustedDrop !== dragIndex) {
            const reordered = [...dayItems];
            const [removed] = reordered.splice(dragIndex, 1);
            reordered.splice(adjustedDrop, 0, removed);

            for (let i = 0; i < reordered.length; i++) {
              if ((reordered[i].itemOrder || reordered[i].item_order || 0) !== i + 1) {
                await updateModuleItem(reordered[i].id, { itemOrder: i + 1 });
              }
            }
          }
        }
      }
      setDraggedItem(null);
      return;
    }

    // Moving to a different day
    const result = await updateModuleItem(draggedItem.id, {
      dueDay: targetDay
    });

    if (result.error) {
      alert(`Failed to move item: ${result.error}`);
    }

    setDraggedItem(null);
  };

  const handleDeleteModule = async (module, e) => {
    e.stopPropagation();
    const weekNum = module.weekNumber || module.week_number;
    if (!window.confirm(`Delete Week ${weekNum}? This will remove all items in this week from ALL cohorts. This action cannot be undone.`)) {
      return;
    }

    const result = await deleteModule(module.id);
    if (result.error) {
      alert(`Failed to delete module: ${result.error}`);
    }
  };

  // Calculate next available week number
  const nextWeekNumber = sortedModules.length > 0 
    ? Math.max(...sortedModules.map(m => m.weekNumber || m.week_number)) + 1 
    : 1;

  return (
    <div className="master-modules">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>📚 Master Modules</h1>
          <p>Manage course content for all cohorts</p>
        </div>
        <div className="header-actions">
          <button 
            className="btn-secondary" 
            onClick={() => {
              setTempZoomUrl(defaultZoomUrl);
              setShowZoomSettingsModal(true);
            }}
          >
            🎥 Zoom Settings
          </button>
          <button className="btn-primary" onClick={() => {
            setNewModule({ weekNumber: nextWeekNumber, title: `Week ${nextWeekNumber}`, isVisible: false });
            setShowAddModuleModal(true);
          }}>
            + Add New Week
          </button>
        </div>
      </div>

      {/* Default Zoom URL Notice */}
      <div className="zoom-url-notice">
        <span>🎥 Default Zoom URL: </span>
        <a href={defaultZoomUrl} target="_blank" rel="noopener noreferrer">{defaultZoomUrl}</a>
        <button className="btn-link" onClick={() => {
          setTempZoomUrl(defaultZoomUrl);
          setShowZoomSettingsModal(true);
        }}>Change</button>
      </div>

      {/* Info Banner */}
      <div className="info-banner" style={{ marginBottom: '24px' }}>
        <span>ℹ️</span>
        <span>Changes made here apply to <strong>all cohorts</strong>. Use Cohort View to control which weeks are visible to each cohort.</span>
      </div>

      {/* ================================================================= */}
      {/* MASTER SYLLABUS & ATTACHMENTS SECTION */}
      {/* ================================================================= */}
      <div className="module-card" style={{ marginBottom: '24px' }}>
        <div 
          className="module-card-header"
          onClick={() => setSyllabusExpanded(!syllabusExpanded)}
          style={{ cursor: 'pointer' }}
        >
          <div className="module-card-left">
            <span className={`expand-icon ${syllabusExpanded ? 'expanded' : ''}`}>▶</span>
            <div className="module-card-info">
              <h3>📎 Master Syllabus & Attachments</h3>
              <span className="item-count">
                {masterPdfs.length} file{masterPdfs.length !== 1 ? 's' : ''} • Visible to all cohorts
              </span>
            </div>
          </div>
          <div className="module-card-right">
            <button
              className="btn-primary"
              style={{ padding: '6px 12px', fontSize: '13px' }}
              onClick={(e) => {
                e.stopPropagation();
                setShowAddPdfModal(true);
              }}
            >
              + Add PDF
            </button>
          </div>
        </div>

        {syllabusExpanded && (
          <div className="module-card-content">
            {isLoadingPdfs ? (
              <div className="empty-items">
                <p>Loading PDFs...</p>
              </div>
            ) : masterPdfs.length === 0 ? (
              <div className="empty-items">
                <p>No master PDFs uploaded yet</p>
                <p style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
                  PDFs added here will appear in the "Syllabus & Attachments" tab for all cohorts
                </p>
              </div>
            ) : (
              <div className="items-list">
                {masterPdfs.map((pdf, index) => (
                  <div key={pdf.id} className="item-row">
                    <div className="item-row-left">
                      <span className="item-order">{index + 1}</span>
                      <span className="item-icon">📄</span>
                      <div className="item-info">
                        <a 
                          href={pdf.file_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="item-title"
                          style={{ color: '#2563eb', textDecoration: 'none' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {pdf.title}
                        </a>
                        <div className="item-meta">
                          <span className="item-type">{pdf.filename}</span>
                          {pdf.file_size && (
                            <span className="item-day">{pdf.file_size}</span>
                          )}
                          {pdf.uploaded_at && (
                            <span className="item-day">
                              Uploaded {new Date(pdf.uploaded_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="item-row-actions">
                      <a
                        href={pdf.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-icon"
                        title="View PDF"
                        onClick={(e) => e.stopPropagation()}
                      >
                        👁️
                      </a>
                      <button 
                        className="btn-icon danger" 
                        title="Delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteMasterPdf(pdf);
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modules List */}
      {sortedModules.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">📚</span>
          <h2>No modules yet</h2>
          <p>Create your first week to get started</p>
          <button className="btn-primary" onClick={() => {
            setNewModule({ weekNumber: 1, title: 'Week 1: Getting Started', isVisible: false });
            setShowAddModuleModal(true);
          }}>
            + Create Week 1
          </button>
        </div>
      ) : (
        <div className="modules-accordion">
          {sortedModules.map(module => {
            const weekNum = module.weekNumber || module.week_number;
            const isExpanded = expandedWeeks[weekNum];
            const items = module.items || [];

            return (
              <div key={module.id} className="module-card">
                {/* Module Header */}
                <div 
                  className="module-card-header"
                  onClick={() => toggleWeekExpanded(weekNum)}
                >
                  <div className="module-card-left">
                    <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
                    <div className="module-card-info">
                      <h3>Week {weekNum}: {module.title?.replace(`Week ${weekNum}: `, '') || 'Untitled'}</h3>
                      <span className="item-count">{items.length} item{items.length !== 1 ? 's' : ''}</span>
                      {module.visibleAfter && !module.isVisible && (
                        <span className="scheduled-date">
                          ⏰ Unlocks: {module.visibleAfter.replace(':', ' of Week ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="module-card-right">
                    <button 
                      className="btn-icon edit-module-btn"
                      title="Edit week title"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModuleModal(module);
                      }}
                    >
                      ✏️
                    </button>
                    <button
                      className={`btn-visibility ${module.isVisible ? 'visible' : 'hidden'}`}
                      title={module.isVisible ? 'Click to hide' : 'Click to show'}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleVisibility(module);
                      }}
                    >
                      {module.isVisible ? '✅ Visible' : '🔒 Hidden'}
                    </button>
                    <button
                      className="btn-icon schedule-btn"
                      title="Schedule visibility"
                      onClick={(e) => {
                        e.stopPropagation();
                        openScheduleModal(module);
                      }}
                    >
                      ⏰
                    </button>
                    <button
                      className="btn-icon danger"
                      title="Delete week"
                      onClick={(e) => handleDeleteModule(module, e)}
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                {/* Module Items (Expanded) - Grouped by Day */}
                {isExpanded && (
                  <div className="module-card-content">
                    {(() => {
                      const daysOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                      const itemsByDay = {};
                      const unscheduledItems = [];
                      
                      // Initialize all days
                      daysOrder.forEach(day => {
                        itemsByDay[day] = [];
                      });
                      
                      items.forEach(item => {
                        const day = item.dueDay || item.due_day;
                        if (day && daysOrder.includes(day)) {
                          itemsByDay[day].push(item);
                        } else {
                          unscheduledItems.push(item);
                        }
                      });

                      // Sort items within each day
                      Object.keys(itemsByDay).forEach(day => {
                        itemsByDay[day].sort((a, b) => (a.itemOrder || a.item_order || 0) - (b.itemOrder || b.item_order || 0));
                      });

                      return (
                        <>
                          {/* Render each day */}
                          {daysOrder.map(day => {
                            const dayItems = itemsByDay[day];
                            const isEmpty = dayItems.length === 0;
                            const isDropTarget = dragOverDay === `${weekNum}-${day}`;

                            return (
                              <div 
                                key={day} 
                                className={`day-group ${isEmpty ? 'empty' : ''} ${isDropTarget ? 'drop-target' : ''}`}
                                onDragOver={(e) => handleDragOver(e, day, weekNum)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, day, weekNum)}
                              >
                                <div className="day-group-header">
                                  <span className="day-group-icon">📅</span>
                                  <span className="day-group-title">{day}</span>
                                  <span className="day-group-count">
                                    {isEmpty ? 'No items' : `${dayItems.length} item${dayItems.length !== 1 ? 's' : ''}`}
                                  </span>
                                  <button
                                    className="btn-add-to-day-small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setNewItem(prev => ({ ...prev, dueDay: day }));
                                      openAddItemModal(weekNum);
                                    }}
                                    title={`Add item to ${day}`}
                                  >
                                    +
                                  </button>
                                </div>
                                {(dayItems.length > 0 || isDropTarget) && (
                                  <div className={`day-group-items ${isDropTarget && isEmpty ? 'drop-placeholder' : ''}`}>
                                    {dayItems.map((item, index) => {
                                      const showDropBefore = draggedItem && draggedItem.id !== item.id && dragOverIndex === `${weekNum}-${day}-${index}`;
                                      const showDropAfter = draggedItem && draggedItem.id !== item.id && index === dayItems.length - 1 && dragOverIndex === `${weekNum}-${day}-${index + 1}`;
                                      const isDragged = draggedItem && draggedItem.id === item.id;

                                      return (
                                        <React.Fragment key={item.id}>
                                          {showDropBefore && (
                                            <div className="drop-indicator" style={{
                                              height: 3,
                                              background: '#4f46e5',
                                              borderRadius: 2,
                                              margin: '2px 8px',
                                              boxShadow: '0 0 6px rgba(79, 70, 229, 0.5)',
                                              transition: 'opacity 0.15s',
                                            }} />
                                          )}
                                          <div 
                                            className={`item-row draggable ${isDragged ? 'dragging-source' : ''}`}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, item, weekNum)}
                                            onDragEnd={handleDragEnd}
                                            onDragOver={(e) => handleItemDragOver(e, day, weekNum, index)}
                                            style={isDragged ? { opacity: 0.4 } : {}}
                                          >
                                            <div className="item-row-left">
                                              <span className="drag-handle" title="Drag to reorder">⋮⋮</span>
                                              <span className="item-icon">{getTypeIcon(item.type)}</span>
                                              <div className="item-info">
                                                <span className="item-title">{item.title}</span>
                                                <div className="item-meta">
                                                  <span className="item-type">{getTypeLabel(item.type)}</span>
                                                  {item.type === 'live-lecture' && (item.startTime || item.start_time) && (
                                                    <span className="item-time" style={{ marginLeft: 8, color: 'var(--gray-500)', fontSize: '0.85em' }}>
                                                      🕐 {item.startTime || item.start_time}
                                                      {(item.endTime || item.end_time) && ` - ${item.endTime || item.end_time}`}
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                            <div className="item-row-actions">
                                              <button 
                                                className="btn-icon" 
                                                title="Edit"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  openEditItemModal(weekNum, item);
                                                }}
                                              >
                                                ✏️
                                              </button>
                                              <button 
                                                className="btn-icon danger" 
                                                title="Delete"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDeleteItem(weekNum, item.id, item.title);
                                                }}
                                              >
                                                🗑️
                                              </button>
                                            </div>
                                          </div>
                                          {showDropAfter && (
                                            <div className="drop-indicator" style={{
                                              height: 3,
                                              background: '#4f46e5',
                                              borderRadius: 2,
                                              margin: '2px 8px',
                                              boxShadow: '0 0 6px rgba(79, 70, 229, 0.5)',
                                              transition: 'opacity 0.15s',
                                            }} />
                                          )}
                                        </React.Fragment>
                                      );
                                    })}
                                    {isDropTarget && isEmpty && (
                                      <div className="drop-hint">Drop here to move to {day}</div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {/* Unscheduled items */}
                          {unscheduledItems.length > 0 && (
                            <div className="day-group unscheduled">
                              <div className="day-group-header">
                                <span className="day-group-icon">⚠️</span>
                                <span className="day-group-title">Unscheduled</span>
                                <span className="day-group-count">{unscheduledItems.length} item{unscheduledItems.length !== 1 ? 's' : ''}</span>
                              </div>
                              <div className="day-group-items">
                                {unscheduledItems.map((item, index) => (
                                  <div key={item.id} className="item-row">
                                    <div className="item-row-left">
                                      <span className="item-order">{index + 1}</span>
                                      <span className="item-icon">{getTypeIcon(item.type)}</span>
                                      <div className="item-info">
                                        <span className="item-title">{item.title}</span>
                                        <div className="item-meta">
                                          <span className="item-type">{getTypeLabel(item.type)}</span>
                                          <span className="item-day missing">⚠️ No day set</span>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="item-row-actions">
                                      <button 
                                        className="btn-icon" 
                                        title="Edit"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openEditItemModal(weekNum, item);
                                        }}
                                      >
                                        ✏️
                                      </button>
                                      <button 
                                        className="btn-icon danger" 
                                        title="Delete"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteItem(weekNum, item.id, item.title);
                                        }}
                                      >
                                        🗑️
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}

                    {/* Add Item Button */}
                    <button 
                      className="btn-add-item"
                      onClick={() => openAddItemModal(weekNum)}
                    >
                      + Add Item to Week {weekNum}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ================================================================= */}
      {/* ADD MASTER PDF MODAL */}
      {/* ================================================================= */}
      {showAddPdfModal && (
        <div className="modal-overlay" onClick={() => setShowAddPdfModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Master PDF</h3>
              <button className="modal-close" onClick={() => setShowAddPdfModal(false)}>×</button>
            </div>

            <form onSubmit={handleUploadMasterPdf}>
              <div className="form-group">
                <label>Title <span className="required">*</span></label>
                <input
                  type="text"
                  placeholder="e.g., Course Syllabus, Study Guide"
                  value={newPdfTitle}
                  onChange={(e) => setNewPdfTitle(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>PDF File <span className="required">*</span></label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setSelectedPdfFile(e.target.files[0])}
                  required
                />
                {selectedPdfFile && (
                  <small className="form-hint">
                    Selected: {selectedPdfFile.name} ({formatFileSize(selectedPdfFile.size)})
                  </small>
                )}
              </div>

              <div className="info-banner" style={{ marginTop: '16px' }}>
                <span>ℹ️</span>
                <span>This PDF will be visible in the <strong>Syllabus & Attachments</strong> tab for ALL cohorts.</span>
              </div>

              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn-secondary" 
                  onClick={() => {
                    setShowAddPdfModal(false);
                    setNewPdfTitle('');
                    setSelectedPdfFile(null);
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary" 
                  disabled={isUploadingPdf || !selectedPdfFile || !newPdfTitle.trim()}
                >
                  {isUploadingPdf ? 'Uploading...' : 'Upload PDF'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Module Modal */}
      {showAddModuleModal && (
        <div className="modal-overlay" onClick={() => setShowAddModuleModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Week</h3>
              <button className="modal-close" onClick={() => setShowAddModuleModal(false)}>×</button>
            </div>

            <form onSubmit={handleAddModule}>
              <div className="form-group">
                <label>Week Number</label>
                <input
                  type="number"
                  min="1"
                  value={newModule.weekNumber}
                  onChange={(e) => setNewModule({ ...newModule, weekNumber: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  placeholder="e.g., Foundations & Getting Started"
                  value={newModule.title}
                  onChange={(e) => setNewModule({ ...newModule, title: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={newModule.isVisible}
                    onChange={(e) => setNewModule({ ...newModule, isVisible: e.target.checked })}
                  />
                  <span>Visible by default (can be overridden per cohort)</span>
                </label>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowAddModuleModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Creating...' : 'Create Week'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Module Modal */}
      {showEditModuleModal && editingModule && (
        <div className="modal-overlay" onClick={() => setShowEditModuleModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Week {editingModule.weekNumber}</h3>
              <button className="modal-close" onClick={() => setShowEditModuleModal(false)}>×</button>
            </div>

            <form onSubmit={handleEditModule}>
              <div className="form-group">
                <label>Week Number</label>
                <input
                  type="number"
                  value={editingModule.weekNumber}
                  disabled
                  className="input-disabled"
                />
                <small className="form-hint">Week number cannot be changed</small>
              </div>

              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  placeholder="e.g., Foundations & Getting Started"
                  value={editingModule.title}
                  onChange={(e) => setEditingModule({ ...editingModule, title: e.target.value })}
                  required
                />
                <small className="form-hint">Will be displayed as "Week {editingModule.weekNumber}: {editingModule.title || '...'}"</small>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={editingModule.isVisible}
                    onChange={(e) => setEditingModule({ ...editingModule, isVisible: e.target.checked })}
                  />
                  <span>Visible by default (can be overridden per cohort)</span>
                </label>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowEditModuleModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Item Modal */}
      {showAddItemModal && (
        <div className="modal-overlay" onClick={() => setShowAddItemModal(false)}>
          <div className="modal large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Item to Week {selectedWeek}</h3>
              <button className="modal-close" onClick={() => setShowAddItemModal(false)}>×</button>
            </div>

            <form onSubmit={handleAddItem}>
              {/* Type and Day - Always shown */}
              <div className="form-row">
                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={newItem.type}
                    onChange={(e) => setNewItem({ ...newItem, type: e.target.value })}
                  >
                    <option value="start-here">🚀 Start Here</option>
                    <option value="about-course">ℹ️ About Course</option>
                    <option value="video">🎬 Video</option>
                    <option value="live-lecture">📡 Live Lecture</option>
                    <option value="assignment">📝 Assignment</option>
                    <option value="quiz">❓ Quiz</option>
                    <option value="exam">📋 Exam</option>
                    <option value="workbook">📒 Workbook</option>
                    <option value="resource">🔗 Resource</option>
                    <option value="reflection">💭 Reflection</option>
                  </select>
                </div>
                {!(newItem.type === 'live-lecture' && newItem.isRecurring && newItem.recurringDays) && (
                <div className="form-group">
                  <label>Day of Week <span className="required">*</span></label>
                  <select
                    value={newItem.dueDay}
                    onChange={(e) => setNewItem({ ...newItem, dueDay: e.target.value })}
                    required
                  >
                    <option value="">Select day...</option>
                    <option value="Monday">Monday</option>
                    <option value="Tuesday">Tuesday</option>
                    <option value="Wednesday">Wednesday</option>
                    <option value="Thursday">Thursday</option>
                    <option value="Friday">Friday</option>
                    <option value="Saturday">Saturday</option>
                    <option value="Sunday">Sunday</option>
                  </select>
                  <p className="help-text" style={{ marginTop: '6px', fontSize: '12px', color: 'var(--gray-600)' }}>
                    📅 Items with a day assigned will automatically appear on each cohort's calendar.
                  </p>
                </div>
                )}
              </div>

              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  placeholder="e.g., Introduction to Pharmacology"
                  value={newItem.title}
                  onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                  required
                />
              </div>

              {/* Content field for text-based items */}
              {['start-here', 'about-course', 'resource'].includes(newItem.type) && (
                <div className="form-group">
                  <label>Content</label>
                  <textarea
                    rows={3}
                    placeholder="Enter the content text..."
                    value={newItem.content}
                    onChange={(e) => setNewItem({ ...newItem, content: e.target.value })}
                  />
                </div>
              )}

              {/* Video fields */}
              {newItem.type === 'video' && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Video Type</label>
                      <select
                        value={newItem.videoType}
                        onChange={(e) => setNewItem({ ...newItem, videoType: e.target.value })}
                      >
                        <option value="youtube">📺 YouTube</option>
                        <option value="url">🔗 External URL</option>
                        <option value="upload">📤 Upload</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Video URL</label>
                    <input
                      type="url"
                      placeholder="https://youtube.com/watch?v=..."
                      value={newItem.videoUrl}
                      onChange={(e) => setNewItem({ ...newItem, videoUrl: e.target.value })}
                    />
                  </div>
                </>
              )}

              {/* Live Lecture fields */}
              {newItem.type === 'live-lecture' && (
                <>
                  <div className="form-group">
                    <label>Description</label>
                    <RichTextEditor
                      value={newItem.description}
                      onChange={(html) => setNewItem({ ...newItem, description: html })}
                      placeholder="Lecture topic or notes..."
                      rows={3}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Start Time</label>
                      <input
                        type="time"
                        value={newItem.startTime || ''}
                        onChange={(e) => setNewItem({ ...newItem, startTime: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>End Time</label>
                      <input
                        type="time"
                        value={newItem.endTime || ''}
                        onChange={(e) => setNewItem({ ...newItem, endTime: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Meeting URL</label>
                    <input
                      type="url"
                      placeholder={`Leave empty to use default: ${defaultZoomUrl}`}
                      value={newItem.videoUrl || ''}
                      onChange={(e) => setNewItem({ ...newItem, videoUrl: e.target.value })}
                    />
                    <span className="form-hint">
                      {newItem.videoUrl ? '✓ Using custom URL' : `📎 Will use default Zoom URL`}
                    </span>
                  </div>
                  
                  {/* Recurring option */}
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={newItem.isRecurring || false}
                        onChange={(e) => setNewItem({ ...newItem, isRecurring: e.target.checked })}
                        style={{ marginRight: '8px' }}
                      />
                      Create recurring lectures
                    </label>
                    {newItem.isRecurring && (
                      <div className="recurring-options">
                        <select
                          value={newItem.recurringDays || ''}
                          onChange={(e) => setNewItem({ ...newItem, recurringDays: e.target.value })}
                        >
                          <option value="">Select days...</option>
                          <option value="monday">Every Monday</option>
                          <option value="tuesday">Every Tuesday</option>
                          <option value="wednesday">Every Wednesday</option>
                          <option value="thursday">Every Thursday</option>
                          <option value="friday">Every Friday</option>
                          <option value="mwf">Mon/Wed/Fri</option>
                          <option value="tth">Tue/Thu</option>
                          <option value="daily">Every Day</option>
                        </select>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Assessment fields - Description and Points */}
              {['assignment', 'quiz', 'exam'].includes(newItem.type) && (
                <>
                  <div className="form-group">
                    <label>Description / Instructions</label>
                    <RichTextEditor
                      value={newItem.description}
                      onChange={(html) => setNewItem({ ...newItem, description: html })}
                      placeholder="Instructions or details... Use the toolbar for bullets, bold, italics, etc."
                      rows={4}
                    />
                  </div>
                  
                  {/* File Upload for assignments - supports up to 10 files */}
                  {newItem.type === 'assignment' && (
                    <div className="form-group">
                      <label>Attach Documents (optional, up to 10)</label>
                      <div className="pdf-upload-area">
                        {selectedFilesForItem.length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '8px' }}>
                            {selectedFilesForItem.map((file, idx) => (
                              <div key={idx} className="selected-pdf" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'var(--gray-50)', borderRadius: '6px' }}>
                                <span>📄 {file.name}</span>
                                <button 
                                  type="button" 
                                  className="btn-remove-pdf"
                                  onClick={() => setSelectedFilesForItem(prev => prev.filter((_, i) => i !== idx))}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {selectedFilesForItem.length < 10 && (
                          <label className="pdf-upload-label">
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
                              multiple
                              onChange={(e) => {
                                const newFiles = Array.from(e.target.files);
                                setSelectedFilesForItem(prev => [...prev, ...newFiles].slice(0, 10));
                                e.target.value = '';
                              }}
                              style={{ display: 'none' }}
                            />
                            <span className="upload-btn">📤 {selectedFilesForItem.length > 0 ? 'Add More Files' : 'Choose Files'}</span>
                            <span className="upload-hint">Students will be able to download these files ({selectedFilesForItem.length}/10)</span>
                          </label>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Points</label>
                      <input
                        type="number"
                        min="0"
                        value={newItem.points}
                        onChange={(e) => setNewItem({ ...newItem, points: e.target.value })}
                      />
                    </div>
                    {['quiz', 'exam'].includes(newItem.type) && (
                      <>
                        <div className="form-group">
                          <label>Number of Questions</label>
                          <input
                            type="number"
                            min="1"
                            value={newItem.questionCount}
                            onChange={(e) => setNewItem({ ...newItem, questionCount: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Time Limit (minutes)</label>
                          <input
                            type="number"
                            min="0"
                            value={newItem.timeLimit}
                            onChange={(e) => setNewItem({ ...newItem, timeLimit: e.target.value })}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}

              {/* Workbook fields */}
              {newItem.type === 'workbook' && (
                <>
                  <div className="form-group">
                    <label>Description (optional)</label>
                    <RichTextEditor
                      value={newItem.description}
                      onChange={(html) => setNewItem({ ...newItem, description: html })}
                      placeholder="Instructions for this workbook..."
                      rows={3}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Upload Documents <span className="required">*</span> (up to 10)</label>
                    <div className="pdf-upload-area">
                      {selectedFilesForItem.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '8px' }}>
                          {selectedFilesForItem.map((file, idx) => (
                            <div key={idx} className="selected-pdf" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'var(--gray-50)', borderRadius: '6px' }}>
                              <span>📄 {file.name}</span>
                              <button 
                                type="button" 
                                className="btn-remove-pdf"
                                onClick={() => setSelectedFilesForItem(prev => prev.filter((_, i) => i !== idx))}
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {selectedFilesForItem.length < 10 && (
                        <label className="pdf-upload-label">
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
                            multiple
                            onChange={(e) => {
                              const newFiles = Array.from(e.target.files);
                              setSelectedFilesForItem(prev => [...prev, ...newFiles].slice(0, 10));
                              e.target.value = '';
                            }}
                            style={{ display: 'none' }}
                          />
                          <span className="upload-btn">📤 {selectedFilesForItem.length > 0 ? 'Add More Files' : 'Choose Files'}</span>
                          <span className="upload-hint">Students will download, complete, and upload back ({selectedFilesForItem.length}/10)</span>
                        </label>
                      )}
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Points (optional)</label>
                      <input
                        type="number"
                        min="0"
                        value={newItem.points}
                        onChange={(e) => setNewItem({ ...newItem, points: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Number of Questions</label>
                      <input
                        type="number"
                        min="1"
                        value={newItem.questionCount}
                        onChange={(e) => setNewItem({ ...newItem, questionCount: e.target.value })}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Reflection fields - questions editor */}
              {newItem.type === 'reflection' && (
                <div className="form-group" style={{ marginTop: '8px' }}>
                  <label>Reflection Questions <span className="required">*</span></label>
                  <p style={{ fontSize: '12px', color: 'var(--gray-500)', margin: '0 0 8px' }}>
                    Add the questions students will answer for this week's reflection.
                    You can edit, reorder, or add more later under Reflections in the sidebar.
                  </p>
                  {reflectionQuestions.map((q, i) => (
                    <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'flex-start' }}>
                      <span style={{ minWidth: '24px', paddingTop: '10px', fontWeight: '600', color: 'var(--gray-500)', fontSize: '13px' }}>{i + 1}.</span>
                      <textarea
                        rows={2}
                        value={q}
                        onChange={(e) => {
                          const updated = [...reflectionQuestions];
                          updated[i] = e.target.value;
                          setReflectionQuestions(updated);
                        }}
                        placeholder={`Enter reflection question ${i + 1}...`}
                        style={{ flex: 1, padding: '8px 12px', border: '1px solid var(--gray-300)', borderRadius: '6px', fontSize: '14px', resize: 'vertical', fontFamily: 'inherit' }}
                      />
                      {reflectionQuestions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setReflectionQuestions(prev => prev.filter((_, idx) => idx !== i))}
                          style={{ padding: '8px', background: 'transparent', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', color: '#dc2626', fontSize: '14px' }}
                          title="Remove question"
                        >
                          &times;
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setReflectionQuestions(prev => [...prev, ''])}
                    className="btn-secondary"
                    style={{ fontSize: '13px', padding: '6px 12px' }}
                  >
                    + Add Another Question
                  </button>
                </div>
              )}

              {['assignment', 'quiz', 'exam', 'workbook'].includes(newItem.type) && (
                <EligibilityToggle
                  checked={newItem.countsForEligibility || false}
                  onChange={(val) => setNewItem({ ...newItem, countsForEligibility: val })}
                />
              )}

              <div className="info-banner" style={{ marginTop: '16px' }}>
                <span>ℹ️</span>
                <span>This item will appear on <strong>{newItem.dueDay || '(select a day)'}</strong> of Week {selectedWeek} for all cohorts, based on each cohort's start date.</span>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowAddItemModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Adding...' : 'Add Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Item Modal */}
      {showEditItemModal && editingItem && (
        <div className="modal-overlay" onClick={() => setShowEditItemModal(false)}>
          <div className="modal large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Item</h3>
              <button className="modal-close" onClick={() => setShowEditItemModal(false)}>×</button>
            </div>

            <form onSubmit={handleEditItem}>
              {/* Type (read-only) and Day of Week */}
              <div className="form-row">
                <div className="form-group">
                  <label>Type</label>
                  <input type="text" value={getTypeLabel(editingItem.type)} disabled className="input-disabled" />
                </div>
                <div className="form-group">
                  <label>Day of Week <span className="required">*</span></label>
                  <select
                    value={editingItem.dueDay || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, dueDay: e.target.value })}
                    required
                  >
                    <option value="">Select day...</option>
                    <option value="Monday">Monday</option>
                    <option value="Tuesday">Tuesday</option>
                    <option value="Wednesday">Wednesday</option>
                    <option value="Thursday">Thursday</option>
                    <option value="Friday">Friday</option>
                    <option value="Saturday">Saturday</option>
                    <option value="Sunday">Sunday</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={editingItem.title}
                  onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                  required
                />
              </div>

              {/* Content field for text-based items */}
              {['start-here', 'about-course', 'resource'].includes(editingItem.type) && (
                <div className="form-group">
                  <label>Content</label>
                  <textarea
                    rows={3}
                    value={editingItem.content || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, content: e.target.value })}
                  />
                </div>
              )}

              {/* Video fields */}
              {editingItem.type === 'video' && (
                <>
                  <div className="form-group">
                    <label>Description</label>
                    <RichTextEditor
                      value={editingItem.description || ''}
                      onChange={(html) => setEditingItem({ ...editingItem, description: html })}
                      placeholder="Description or notes..."
                      rows={3}
                    />
                  </div>
                  <div className="form-group">
                    <label>Video URL</label>
                    <input
                      type="url"
                      placeholder="https://youtube.com/watch?v=..."
                      value={editingItem.videoUrl || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, videoUrl: e.target.value })}
                    />
                  </div>
                </>
              )}

              {/* Live Lecture edit fields */}
              {editingItem.type === 'live-lecture' && (
                <>
                  <div className="form-group">
                    <label>Description</label>
                    <RichTextEditor
                      value={editingItem.description || ''}
                      onChange={(html) => setEditingItem({ ...editingItem, description: html })}
                      placeholder="Lecture topic or notes..."
                      rows={3}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Start Time</label>
                      <input
                        type="time"
                        value={editingItem.startTime || ''}
                        onChange={(e) => setEditingItem({ ...editingItem, startTime: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>End Time</label>
                      <input
                        type="time"
                        value={editingItem.endTime || ''}
                        onChange={(e) => setEditingItem({ ...editingItem, endTime: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Meeting URL</label>
                    <input
                      type="url"
                      placeholder={`Leave empty to use default: ${defaultZoomUrl}`}
                      value={editingItem.videoUrl || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, videoUrl: e.target.value })}
                    />
                    <span className="form-hint">
                      {editingItem.videoUrl ? '✓ Using custom URL' : `📎 Will use default Zoom URL`}
                    </span>
                  </div>
                </>
              )}

              {/* Workbook edit fields */}
              {editingItem.type === 'workbook' && (
                <>
                  <div className="form-group">
                    <label>Description / Instructions</label>
                    <RichTextEditor
                      value={editingItem.description || ''}
                      onChange={(html) => setEditingItem({ ...editingItem, description: html })}
                      placeholder="Instructions for this workbook..."
                      rows={3}
                    />
                  </div>

                  <div className="form-group">
                    <label>Attached Documents (up to 10)</label>
                    {/* Show existing attachments */}
                    {(() => {
                      const existingFiles = !removePdfOnEdit 
                        ? (editingItem.attachments || (editingItem.pdfUrl ? [{ url: editingItem.pdfUrl, name: editingItem.pdfName || 'Attached PDF' }] : []))
                        : [];
                      return (
                        <>
                          {existingFiles.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '8px' }}>
                              {existingFiles.map((file, idx) => (
                                <div key={`existing-${idx}`} className="selected-pdf" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'var(--gray-50)', borderRadius: '6px' }}>
                                  <span>📄 {file.name}</span>
                                  <a href={file.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: 'var(--blue-primary)' }}>View</a>
                                  <button 
                                    type="button" 
                                    className="btn-remove-pdf"
                                    onClick={() => {
                                      if (editingItem.attachments && editingItem.attachments.length > 1) {
                                        const updated = editingItem.attachments.filter((_, i) => i !== idx);
                                        setEditingItem({ ...editingItem, attachments: updated });
                                      } else {
                                        setRemovePdfOnEdit(true);
                                      }
                                    }}
                                    title="Remove file"
                                  >
                                    ✕
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          {/* Show newly selected files for upload */}
                          {selectedFilesForEdit.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '8px' }}>
                              {selectedFilesForEdit.map((file, idx) => (
                                <div key={`new-${idx}`} className="selected-pdf" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'var(--gray-50)', borderRadius: '6px' }}>
                                  <span>📄 {file.name} <em style={{ fontSize: '11px', color: 'var(--gray-500)' }}>(new)</em></span>
                                  <button 
                                    type="button" 
                                    className="btn-remove-pdf"
                                    onClick={() => setSelectedFilesForEdit(prev => prev.filter((_, i) => i !== idx))}
                                  >
                                    ✕
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          {/* Add more files button */}
                          {(existingFiles.length + selectedFilesForEdit.length) < 10 && (
                            <div className="pdf-upload-area">
                              <label className="pdf-upload-label">
                                <input
                                  type="file"
                                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
                                  multiple
                                  onChange={(e) => {
                                    const newFiles = Array.from(e.target.files);
                                    const maxNew = 10 - existingFiles.length - selectedFilesForEdit.length;
                                    setSelectedFilesForEdit(prev => [...prev, ...newFiles].slice(0, prev.length + maxNew));
                                    setRemovePdfOnEdit(false);
                                    e.target.value = '';
                                  }}
                                  style={{ display: 'none' }}
                                />
                                <span className="upload-btn">📤 {existingFiles.length > 0 || selectedFilesForEdit.length > 0 ? 'Add More Files' : 'Choose Files'}</span>
                                <span className="upload-hint">{removePdfOnEdit ? 'Previous files will be removed' : `${existingFiles.length + selectedFilesForEdit.length}/10 files`}</span>
                              </label>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Points (optional)</label>
                      <input
                        type="number"
                        min="0"
                        value={editingItem.points || 0}
                        onChange={(e) => setEditingItem({ ...editingItem, points: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Number of Questions</label>
                      <input
                        type="number"
                        min="0"
                        value={editingItem.questionCount || 0}
                        onChange={(e) => setEditingItem({ ...editingItem, questionCount: e.target.value })}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Assessment fields - Description, Points, PDF */}
              {['assignment', 'quiz', 'exam'].includes(editingItem.type) && (
                <>
                  <div className="form-group">
                    <label>Description / Instructions</label>
                    <RichTextEditor
                      value={editingItem.description || ''}
                      onChange={(html) => setEditingItem({ ...editingItem, description: html })}
                      placeholder="Instructions or details... Use the toolbar for bullets, bold, italics, etc."
                      rows={4}
                    />
                  </div>

                  {/* File attachments for assignments */}
                  {editingItem.type === 'assignment' && (
                    <div className="form-group">
                      <label>Attached Documents (up to 10)</label>
                      {(() => {
                        const existingFiles = !removePdfOnEdit 
                          ? (editingItem.attachments || (editingItem.pdfUrl ? [{ url: editingItem.pdfUrl, name: editingItem.pdfName || 'Attached PDF' }] : []))
                          : [];
                        return (
                          <>
                            {existingFiles.length > 0 && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '8px' }}>
                                {existingFiles.map((file, idx) => (
                                  <div key={`existing-${idx}`} className="selected-pdf" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'var(--gray-50)', borderRadius: '6px' }}>
                                    <span>📄 {file.name}</span>
                                    <a href={file.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: 'var(--blue-primary)' }}>View</a>
                                    <button 
                                      type="button" 
                                      className="btn-remove-pdf"
                                      onClick={() => {
                                        if (editingItem.attachments && editingItem.attachments.length > 1) {
                                          const updated = editingItem.attachments.filter((_, i) => i !== idx);
                                          setEditingItem({ ...editingItem, attachments: updated });
                                        } else {
                                          setRemovePdfOnEdit(true);
                                        }
                                      }}
                                      title="Remove file"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                            {selectedFilesForEdit.length > 0 && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '8px' }}>
                                {selectedFilesForEdit.map((file, idx) => (
                                  <div key={`new-${idx}`} className="selected-pdf" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'var(--gray-50)', borderRadius: '6px' }}>
                                    <span>📄 {file.name} <em style={{ fontSize: '11px', color: 'var(--gray-500)' }}>(new)</em></span>
                                    <button 
                                      type="button" 
                                      className="btn-remove-pdf"
                                      onClick={() => setSelectedFilesForEdit(prev => prev.filter((_, i) => i !== idx))}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                            {(existingFiles.length + selectedFilesForEdit.length) < 10 && (
                              <div className="pdf-upload-area">
                                <label className="pdf-upload-label">
                                  <input
                                    type="file"
                                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
                                    multiple
                                    onChange={(e) => {
                                      const newFiles = Array.from(e.target.files);
                                      const maxNew = 10 - existingFiles.length - selectedFilesForEdit.length;
                                      setSelectedFilesForEdit(prev => [...prev, ...newFiles].slice(0, prev.length + maxNew));
                                      setRemovePdfOnEdit(false);
                                      e.target.value = '';
                                    }}
                                    style={{ display: 'none' }}
                                  />
                                  <span className="upload-btn">📤 {existingFiles.length > 0 || selectedFilesForEdit.length > 0 ? 'Add More Files' : 'Choose Files'}</span>
                                  <span className="upload-hint">{`${existingFiles.length + selectedFilesForEdit.length}/10 files`}</span>
                                </label>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}

                  <div className="form-row">
                    <div className="form-group">
                      <label>Points</label>
                      <input
                        type="number"
                        min="0"
                        value={editingItem.points || 0}
                        onChange={(e) => setEditingItem({ ...editingItem, points: e.target.value })}
                      />
                    </div>
                    {['quiz', 'exam'].includes(editingItem.type) && (
                      <>
                        <div className="form-group">
                          <label>Number of Questions</label>
                          <input
                            type="number"
                            min="1"
                            value={editingItem.questionCount || 0}
                            onChange={(e) => setEditingItem({ ...editingItem, questionCount: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Time Limit (minutes)</label>
                          <input
                            type="number"
                            min="0"
                            value={editingItem.timeLimit || 0}
                            onChange={(e) => setEditingItem({ ...editingItem, timeLimit: e.target.value })}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}

              {/* Lecture type fields */}
              {editingItem.type === 'lecture' && (
                <div className="form-group">
                  <label>Description</label>
                  <RichTextEditor
                    value={editingItem.description || ''}
                    onChange={(html) => setEditingItem({ ...editingItem, description: html })}
                    placeholder="Lecture notes or description..."
                    rows={3}
                  />
                </div>
              )}

              {/* PDF type fields */}
              {editingItem.type === 'pdf' && (
                <>
                  <div className="form-group">
                    <label>Description</label>
                    <RichTextEditor
                      value={editingItem.description || ''}
                      onChange={(html) => setEditingItem({ ...editingItem, description: html })}
                      placeholder="Description..."
                      rows={2}
                    />
                  </div>
                  <div className="form-group">
                    <label>PDF URL</label>
                    <input
                      type="url"
                      value={editingItem.videoUrl || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, videoUrl: e.target.value })}
                    />
                  </div>
                </>
              )}

              {/* Discussion / Announcement fields */}
              {['discussion', 'announcement'].includes(editingItem.type) && (
                <div className="form-group">
                  <label>Description</label>
                  <RichTextEditor
                    value={editingItem.description || ''}
                    onChange={(html) => setEditingItem({ ...editingItem, description: html })}
                    placeholder="Description..."
                    rows={3}
                  />
                </div>
              )}

              {['assignment', 'quiz', 'exam', 'workbook'].includes(editingItem.type) && (
                <EligibilityToggle
                  checked={editingItem.countsForEligibility || false}
                  onChange={(val) => setEditingItem({ ...editingItem, countsForEligibility: val })}
                />
              )}

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowEditItemModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Schedule Visibility Modal */}
      {showScheduleModal && schedulingModule && (
        <div className="modal-overlay" onClick={() => setShowScheduleModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Schedule Visibility - Week {schedulingModule.weekNumber || schedulingModule.week_number}</h3>
              <button className="modal-close" onClick={() => setShowScheduleModal(false)}>×</button>
            </div>

            <form onSubmit={handleScheduleVisibility}>
              <div className="info-banner" style={{ marginBottom: '16px' }}>
                <span>ℹ️</span>
                <span>Set when this week becomes visible relative to the course timeline. This applies to all cohorts based on their start date.</span>
              </div>

              <p className="schedule-label">Make Week {schedulingModule.weekNumber || schedulingModule.week_number} visible on:</p>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Week</label>
                  <select
                    value={scheduleWeek}
                    onChange={(e) => setScheduleWeek(e.target.value)}
                    required
                  >
                    <option value="">Select week...</option>
                    {[1, 2, 3, 4, 5, 6].map(w => (
                      <option key={w} value={w} disabled={w >= (schedulingModule.weekNumber || schedulingModule.week_number)}>
                        Week {w} {w >= (schedulingModule.weekNumber || schedulingModule.week_number) ? '(must be earlier)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Day</label>
                  <select
                    value={scheduleDay}
                    onChange={(e) => setScheduleDay(e.target.value)}
                    required
                  >
                    <option value="">Select day...</option>
                    <option value="Monday">Monday</option>
                    <option value="Tuesday">Tuesday</option>
                    <option value="Wednesday">Wednesday</option>
                    <option value="Thursday">Thursday</option>
                    <option value="Friday">Friday</option>
                    <option value="Saturday">Saturday</option>
                    <option value="Sunday">Sunday</option>
                  </select>
                </div>
              </div>

              {scheduleWeek && scheduleDay && (
                <div className="schedule-preview">
                  <strong>Week {schedulingModule.weekNumber || schedulingModule.week_number}</strong> will become visible on <strong>{scheduleDay} of Week {scheduleWeek}</strong>
                  <p className="schedule-example">
                    Example: If a cohort starts Monday Jan 6, this week unlocks on{' '}
                    {scheduleDay} Jan {6 + ((parseInt(scheduleWeek) - 1) * 7) + ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].indexOf(scheduleDay)}
                  </p>
                </div>
              )}

              <div className="modal-footer">
                {schedulingModule.visibleAfter && (
                  <button 
                    type="button" 
                    className="btn-danger-outline" 
                    onClick={handleClearSchedule}
                    disabled={isSubmitting}
                  >
                    Clear Schedule
                  </button>
                )}
                <button 
                  type="button" 
                  className="btn-secondary" 
                  onClick={() => setShowScheduleModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={isSubmitting || !scheduleWeek || !scheduleDay}>
                  {isSubmitting ? 'Saving...' : 'Schedule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Zoom Settings Modal */}
      {showZoomSettingsModal && (
        <div className="modal-overlay" onClick={() => setShowZoomSettingsModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🎥 Default Zoom Settings</h3>
              <button className="modal-close" onClick={() => setShowZoomSettingsModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '16px', color: 'var(--gray-600)' }}>
                This URL will be used for all live lectures that don't have a custom URL set.
              </p>
              <div className="form-group">
                <label>Default Zoom Meeting URL</label>
                <input
                  type="url"
                  value={tempZoomUrl}
                  onChange={(e) => setTempZoomUrl(e.target.value)}
                  placeholder="https://zoom.us/j/..."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowZoomSettingsModal(false)}>
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={saveDefaultZoomUrl}
                disabled={isSavingZoomUrl || !tempZoomUrl}
              >
                {isSavingZoomUrl ? 'Saving...' : 'Save Default URL'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterModules;
